/**
 * Single source of truth for building tenant-prefixed URLs.
 *
 * Deliberately does NOT read accountName from useParams()/the current URL —
 * if any past navigation put the wrong value in the URL, reading it back
 * out just repeats and compounds that mistake (e.g. /users/users).
 *
 * accountName is set exactly once, in AccountResolver, right after the
 * backend confirms it via /auth/validate-account. Every other part of the
 * app should treat that stored value as ground truth.
 */

export function getStoredAccountName(): string | null {
    return sessionStorage.getItem('accountName');
}

/**
 * Converts an account name into a clean, URL-friendly slug.
 * Replaces spaces with hyphens so the browser never shows %20 in the
 * address bar. Any other unsafe characters are still encoded, but plain
 * spaces (the common case) become readable hyphens instead.
 *
 * slugify('Test Account (MortgageQC)') -> 'Test-Account-(MortgageQC)'
 */
export function slugify(accountName: string): string {
    return accountName.trim().replace(/\s+/g, '-');
}

/**
 * Builds an absolute path prefixed with the current tenant's account name.
 *
 * getTenantPath('users')         -> "/Test-Account-(MortgageQC)/users"
 * getTenantPath('AuditView/123') -> "/Test-Account-(MortgageQC)/AuditView/123"
 * getTenantPath('.')             -> "/Test-Account-(MortgageQC)"   (dashboard/root)
 */
export function getTenantPath(path: string): string {
    const accountName = getStoredAccountName();
    const base = accountName ? `/${encodeURIComponent(slugify(accountName))}` : '';

    if (!accountName) {
        console.error('[tenantPath] No accountName in sessionStorage — falling back to bare path.');
        return `/${path === '.' ? '' : path}`;
    }

    return path === '.' ? base : `${base}/${path}`;
}